import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  FiUsers,
  FiTrendingUp,
  FiCheckSquare,
  FiAlertCircle,
  FiBarChart2,
  FiDollarSign,
  FiXCircle,
  FiPieChart,
  FiTrendingDown,
  FiMessageSquare,
  FiRefreshCw,
} from 'react-icons/fi';
import { MdWorkOutline } from 'react-icons/md';

// Import modular components
import {
  StatCard,
  DashboardHeader,
  PerformanceMetrics,
  AdminView,
  ActivityFeed,
  UpcomingEvents
} from './components';

// Import services and utilities
import { fetchDashboardData } from './services/dashboardService';
import { formatCurrency, formatPercentage, formatNumber } from './utils/formatters';

/**
 * MasterDashboard - Main dashboard component for master users
 * Provides a complete view of the system with statistics and metrics
 */
const MasterDashboard = () => {
  // Initialize with safe default values for all properties
  const [stats, setStats] = useState({
    usersCount: { value: 0, change: 0 },
    workersCount: { value: 0, change: 0 },
    adminsCount: { value: 0, change: 0 },
    revenue: { value: 0, change: 0 },
    services: { value: 0, change: 0 },
    totalServices: { value: 0, change: 0 },
    newUsers: { value: 0, change: 0 },
    cancelations: { value: 0, change: 0 },
    conversationCancellations: { value: 0, change: 0 },
    rejectedServices: { value: 0, change: 0 },
    pendingApprovals: { value: 0, change: 0 },
    conversion: { value: 0, change: 0 },
    completedToCanceledRatio: { value: 0, change: 0 },
    completedToRejectedRatio: { value: 0, change: 0 },
  });
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState([]);
  const [adminViewData, setAdminViewData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [error, setError] = useState(null);
  const toast = useToast();

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch data from API
      const data = await fetchDashboardData(timeFrame);
      
      // Check if data contains the expected structure
      if (!data || !data.stats) {
        throw new Error('Estrutura de dados inválida recebida da API');
      }
      
      // Ensure all stats have default values if they're missing
      const safeStats = {
        usersCount: { value: 0, change: 0 },
        workersCount: { value: 0, change: 0 },
        adminsCount: { value: 0, change: 0 },
        revenue: { value: 0, change: 0 },
        services: { value: 0, change: 0 },
        totalServices: { value: 0, change: 0 },
        newUsers: { value: 0, change: 0 },
        cancelations: { value: 0, change: 0 },
        conversationCancellations: { value: 0, change: 0 },
        rejectedServices: { value: 0, change: 0 },
        pendingApprovals: { value: 0, change: 0 },
        conversion: { value: 0, change: 0 },
        completedToCanceledRatio: { value: 0, change: 0 },
        completedToRejectedRatio: { value: 0, change: 0 },
        ...data.stats // Overlay with actual data, keeping defaults for any missing values
      };
      
      // Set the various data pieces
      setStats(safeStats);
      
      if (data.performanceData && Array.isArray(data.performanceData)) {
        setPerformanceData(data.performanceData);
      }
      
      if (data.adminView) {
        setAdminViewData(data.adminView);
      }
      
      if (data.recentActivities && Array.isArray(data.recentActivities)) {
        setRecentActivities(data.recentActivities);
      }
      
      if (data.upcomingEvents && Array.isArray(data.upcomingEvents)) {
        setUpcomingEvents(data.upcomingEvents);
      }
      
      toast({
        title: 'Dados carregados',
        description: 'Dados do dashboard carregados com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(error.message || 'Não foi possível carregar os dados do dashboard.');
      
      toast({
        title: 'Erro ao carregar dados',
        description: error.message || 'Não foi possível carregar os dados do dashboard.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      // Check for authentication errors and redirect if needed
      if (error.message && (
        error.message.includes('Token') || 
        error.message.includes('autenticação') || 
        error.message.includes('login') ||
        error.message.includes('sessão expirou') ||
        error.message.includes('não tem permissão')
      )) {
        // Handle auth-related redirects here if needed
        // window.location.href = '/admin/login';
      }
    } finally {
      setIsLoading(false);
    }
  };

  const prevTimeFrame = useRef(timeFrame);
  const hasFetched = useRef(false);

  useEffect(() => {
    // Skip StrictMode double-mount, but allow timeFrame changes
    if (hasFetched.current && prevTimeFrame.current === timeFrame) return;
    hasFetched.current = true;
    prevTimeFrame.current = timeFrame;
    loadDashboardData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const handleRefresh = () => {
    loadDashboardData();
    toast({
      title: 'Atualizando dados',
      description: 'Os dados do dashboard estão sendo atualizados.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  // Safely access stats properties
  const getValue = (property) => {
    return stats[property]?.value || 0;
  };
  
  const getChange = (property) => {
    return stats[property]?.change || 0;
  };

  // Show error state if there's an error
  if (error) {
    return (
      <Box w="100%" p={4}>
        <Alert 
          status="error" 
          variant="solid" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          textAlign="center" 
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Erro ao carregar dashboard
          </AlertTitle>
          <AlertDescription maxWidth="md">
            {error}
          </AlertDescription>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="white" 
            variant="outline" 
            mt={4}
            onClick={handleRefresh}
          >
            Tentar novamente
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box w="100%" p={4}>
      {/* Dashboard Header */}
      <DashboardHeader 
        timeFrame={timeFrame} 
        onTimeFrameChange={handleTimeFrameChange} 
        onRefresh={handleRefresh} 
      />

      {/* Main Stats */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Usuários Totais" 
          value={getValue('usersCount')} 
          icon={FiUsers} 
          change={getChange('usersCount')} 
          colorScheme="blue" 
          isLoading={isLoading} 
          formatter={formatNumber}
          footerText="Contratantes registrados"
        />
        <StatCard 
          title="Prestadores" 
          value={getValue('workersCount')} 
          icon={MdWorkOutline} 
          change={getChange('workersCount')} 
          colorScheme="teal" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Contratados ativos"
        />
        <StatCard 
          title="Faturamento Total" 
          value={getValue('revenue')} 
          icon={FiDollarSign} 
          change={getChange('revenue')} 
          colorScheme="green" 
          formatter={formatCurrency} 
          isLoading={isLoading}
          footerText="Período atual"
        />
        <StatCard 
          title="Serviços Concluídos" 
          value={getValue('services')} 
          icon={FiCheckSquare} 
          change={getChange('services')} 
          colorScheme="purple" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Serviços Concluídos"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Novos Usuários" 
          value={getValue('newUsers')} 
          icon={FiTrendingUp} 
          change={getChange('newUsers')} 
          colorScheme="cyan" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Novas contas criadas"
        />
        <StatCard 
          title="Cancelamentos" 
          value={getValue('cancelations')} 
          icon={FiAlertCircle} 
          change={getChange('cancelations')} 
          colorScheme="red" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Serviços cancelados"
        />
        <StatCard 
          title="Cancelamentos de Conversa" 
          value={getValue('conversationCancellations')} 
          icon={FiMessageSquare} 
          change={getChange('conversationCancellations')} 
          colorScheme="pink" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Conversas canceladas"
        />
        <StatCard 
          title="Serviços Recusados" 
          value={getValue('rejectedServices')} 
          icon={FiXCircle} 
          change={getChange('rejectedServices')} 
          colorScheme="orange" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Recusas de serviços"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Taxa Concluídos/Cancelados" 
          value={getValue('completedToCanceledRatio')} 
          icon={FiPieChart} 
          change={getChange('completedToCanceledRatio')} 
          colorScheme="green" 
          isLoading={isLoading}
          formatter={formatPercentage}
          footerText="Finalizados ÷ Cancelados"
        />
        <StatCard 
          title="Taxa Concluídos/Recusados" 
          value={getValue('completedToRejectedRatio')} 
          icon={FiTrendingDown} 
          change={getChange('completedToRejectedRatio')} 
          colorScheme="blue" 
          isLoading={isLoading}
          formatter={formatPercentage}
          footerText="Finalizados ÷ Recusados"
        />
        <StatCard 
          title="Taxa de Conversão" 
          value={getValue('conversion')} 
          icon={FiBarChart2} 
          change={getChange('conversion')} 
          colorScheme="orange" 
          formatter={formatPercentage}
          isLoading={isLoading}
          footerText="Visitantes → Clientes"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Aprovações Pendentes" 
          value={getValue('pendingApprovals')} 
          icon={FiUsers} 
          change={getChange('pendingApprovals')} 
          colorScheme="yellow" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Verificações pendentes"
        />
        <StatCard 
          title="Total de Serviços" 
          value={getValue('totalServices')} 
          icon={FiCheckSquare} 
          change={getChange('totalServices')} 
          colorScheme="purple" 
          isLoading={isLoading}
          formatter={formatNumber}
          footerText="Todos os serviços"
        />
      </Grid>

      {/* Charts */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "1fr 1fr" }} gap={6} mb={6}>
        <Card boxShadow="md">
          <CardHeader pb={2}><Heading size="md">Visão Geral</Heading></CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}><Spinner /></Flex>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[
                  { name: 'Contratantes', value: getValue('usersCount') },
                  { name: 'Profissionais', value: getValue('workersCount') },
                  { name: 'Serviços', value: getValue('services') },
                  { name: 'Cancelamentos', value: getValue('cancelations') },
                  { name: 'Recusas', value: getValue('rejectedServices') },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" name="Quantidade" radius={[6, 6, 0, 0]}>
                    <Cell fill="#3182CE" />
                    <Cell fill="#319795" />
                    <Cell fill="#805AD5" />
                    <Cell fill="#E53E3E" />
                    <Cell fill="#DD6B20" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>

        <Card boxShadow="md">
          <CardHeader pb={2}><Heading size="md">Distribuição de Status</Heading></CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}><Spinner /></Flex>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Concluídos', value: getValue('services') || 1 },
                      { name: 'Cancelados', value: getValue('cancelations') || 0 },
                      { name: 'Conv. Canceladas', value: getValue('conversationCancellations') || 0 },
                      { name: 'Recusados', value: getValue('rejectedServices') || 0 },
                    ].filter(d => d.value > 0)}
                    cx="50%" cy="50%" outerRadius={90} dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    <Cell fill="#38A169" />
                    <Cell fill="#E53E3E" />
                    <Cell fill="#D69E2E" />
                    <Cell fill="#DD6B20" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Detailed Analytics */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        <PerformanceMetrics 
          performanceData={performanceData} 
          isLoading={isLoading} 
        />
        <AdminView 
          stats={stats} 
          adminViewData={adminViewData} 
          isLoading={isLoading} 
        />
      </Grid>

      {/* Activity Feed & Calendar */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "1fr 1fr" }} gap={6}>
        <ActivityFeed 
          isLoading={isLoading} 
          activities={recentActivities} 
        />
        <UpcomingEvents 
          isLoading={isLoading} 
          events={upcomingEvents} 
        />
      </Grid>
    </Box>
  );
};

export default MasterDashboard;